import {
  GanttFilter,
  Id,
  OptimizedGantt,
  ProjectElementType,
  ProjectResource,
  resource,
  Sort,
  Tag,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";

type Response = {
  _embedded: {
    gantt: OptimizedGantt;
  };
};

function formatAlloc(
  projectElement: OptimizedGantt[number],
): OptimizedGantt[number] {
  return projectElement.type === ProjectElementType.TASK
    ? {
        ...projectElement,
        allocations: projectElement.allocations.map((alloc) => ({
          ...alloc,
          resource: {
            ...resource,
            ...alloc.resource,
            ...("resourceName" in alloc && {
              name: alloc.resourceName as string,
            }),
            ...("resourceUuid" in alloc && {
              uuid: alloc.resourceUuid as Id,
            }),
          },
        })),
      }
    : projectElement;
}

function formatTags(
  projectElement: OptimizedGantt[number],
): OptimizedGantt[number] {
  return {
    ...projectElement,
    tags: projectElement.tags
      ? projectElement.tags.reduce<Tag[]>(
          (tags, name) => (!!name ? [...tags, { name }] : tags) as Tag[],
          [],
        )
      : [],
  };
}

function getOptimizedGantt(httpClient: HttpClient) {
  return (filter: GanttFilter, sort: Sort, size: number, page: number) => {
    const elTypeMap = {
      list: "ACTIVITY",
      task: "TASK",
      milestone: "MILESTONE",
      event: "EVENT",
      completedWork: "COMPLETED_WORK",
    };
    const sortMap: {
      [Key in Sort]: {
        order: "ASC" | "DESC";
        sortType:
          | "KEY_RANK"
          | "LEXICAL"
          | "START_DATE"
          | "END_DATE"
          | "COMPLETION_DATE";
      };
    } = {
      ascendingName: {
        order: "ASC",
        sortType: "LEXICAL",
      },
      ascendingStartDate: {
        order: "ASC",
        sortType: "START_DATE",
      },
      ascendingEndDate: {
        order: "ASC",
        sortType: "END_DATE",
      },
      ascendingCompletionDate: {
        order: "ASC",
        sortType: "COMPLETION_DATE",
      },
      ascendingCreationDate: {
        order: "ASC",
        sortType: "KEY_RANK",
      },
      ascendingRank: {
        order: "ASC",
        sortType: "KEY_RANK",
      },
      ascendingAuthor: {
        order: "ASC",
        sortType: "KEY_RANK",
      },
      descendingName: {
        order: "DESC",
        sortType: "LEXICAL",
      },
      descendingStartDate: {
        order: "DESC",
        sortType: "START_DATE",
      },
      descendingEndDate: {
        order: "DESC",
        sortType: "END_DATE",
      },
      descendingCompletionDate: {
        order: "DESC",
        sortType: "COMPLETION_DATE",
      },
      descendingCreationDate: {
        order: "DESC",
        sortType: "KEY_RANK",
      },
      descendingRank: {
        order: "DESC",
        sortType: "KEY_RANK",
      },
      descendingAuthor: {
        order: "DESC",
        sortType: "KEY_RANK",
      },
    };

    return httpClient
      .sendHttpRequest<Response>({
        endpoint: `/gantt`,
        method: HttpMethods.POST,
        data: {
          pageNumber: page,
          pageSize: size,
          projectUuids: filter.projects
            ? filter.projects.map(({ uuid }) => uuid)
            : undefined,
          elementScope: filter.scope.toUpperCase(),
          shows: filter.elementTypes
            ? filter.elementTypes.map((type) => elTypeMap[type])
            : ["ACTIVITY", "TASK", "MILESTONE", "EVENT"],
          type: "DETAILS",
          ...sortMap[sort],
          ...(filter.projectsElementsTags &&
            filter.projectsElementsTags.length > 0 && {
              tags: filter.projectsElementsTags.map(({ name }) => name),
            }),
          ...(filter.resourceTags &&
            filter.resourceTags.length > 0 && {
              resourceTags: filter.resourceTags.map(({ name }) => name),
            }),
          ...(filter.resources &&
            filter.resources.length > 0 && {
              resourceScope: "CUSTOM",
              resourceUuids: filter.resources.map(({ uuid }) => uuid),
            }),
          ...(filter.groups &&
            filter.groups.length > 0 && {
              resourceScope: "CUSTOM",
              resourceUuids: filter.groups.reduce<string[]>(
                (resources, group) => [
                  ...resources,
                  ...(group.resourceType === "project"
                    ? (group.resources as ProjectResource[]).map(
                        ({ resource }) => resource.uuid,
                      )
                    : group.resources.map(({ uuid }) => uuid)),
                ],
                [],
              ),
            }),
          includeTemplates:
            !!filter.projects &&
            !!filter.projects.find((project) => project.isTemplate),
        },
      })
      .then(({ _embedded: { gantt } }) =>
        gantt.map((projectElement) => formatTags(formatAlloc(projectElement))),
      );
  };
}

export { getOptimizedGantt };
